(function () {
    'use strict';

    const {checkoutMeasurementContext, checkoutFailureMessage} = require('./subscription.js');
    const {enhancePurchaseOfferPreview, resetPurchaseOfferScroll} = require('./modules/purchase-offer-preview.js');
    const {recordPurchaseStage} = require('./modules/purchase-funnel.js');

    function enhanceReadingOffer(documentObject, windowObject, fetchImplementation = windowObject.fetch?.bind(windowObject), offer = 'pdf') {
        const source = {pdf: 'birthchart_pdf', ai: 'birthchart_ai_chat', aspects: 'birthchart_aspects'}[offer];
        const selector = part => `[data-am-${offer}-offer${part ? `-${part}` : ''}]`;
        const dialog = documentObject.querySelector(selector());
        // Preserve the ordinary subscription link when native modal semantics are unavailable.
        if (!dialog || typeof dialog.showModal !== 'function' || dialog.dataset.readingOfferEnhanced) {
            return null;
        }
        dialog.dataset.readingOfferEnhanced = 'true';
        enhancePurchaseOfferPreview(dialog, windowObject);
        const button = dialog.querySelector(selector('checkout'));
        const status = dialog.querySelector(selector('status'));
        const error = dialog.querySelector(selector('error'));
        const errorMessage = dialog.querySelector(selector('error-message'));
        let busy = false;
        let trigger = null;
        let previousOverflow = '';

        function track(eventName) {
            try {
                windowObject.astrolooAnalytics?.trackEvent(`birthchart_${offer}_offer_${eventName}`, {
                    subscription_source: source,
                    account_state: dialog.dataset.accountState,
                    transport_type: 'beacon',
                });
            } catch (_) {
                // Measurement must never prevent opening the offer or checkout.
            }
        }

        function setBusy(value) {
            busy = value;
            if (button) {
                button.disabled = value;
                button.setAttribute('aria-busy', String(value));
            }
            status.hidden = !value;
        }

        function open(opener) {
            if (dialog.open) return;
            trigger = opener;
            error.hidden = true;
            dialog.showModal();
            previousOverflow = documentObject.documentElement.style.overflow;
            documentObject.documentElement.style.overflow = 'hidden';
            dialog.querySelector(selector('close')).focus();
            resetPurchaseOfferScroll(dialog);
            track('view');
        }

        function close() {
            dialog.close();
        }

        dialog.addEventListener('close', () => {
            documentObject.documentElement.style.overflow = previousOverflow;
            trigger?.focus();
        });
        dialog.addEventListener('click', event => {
            if (event.target === dialog) close();
        });
        dialog.addEventListener('keydown', event => {
            if (event.key !== 'Tab') return;
            const controls = Array.from(dialog.querySelectorAll('button:not([disabled]), a[href], summary'))
                .filter(control => control.getClientRects().length > 0);
            const first = controls[0];
            const last = controls[controls.length - 1];
            if (event.shiftKey && documentObject.activeElement === first) {
                event.preventDefault();
                last?.focus();
            } else if (!event.shiftKey && documentObject.activeElement === last) {
                event.preventDefault();
                first?.focus();
            }
        });
        for (const control of dialog.querySelectorAll(selector('close'))) {
            control.addEventListener('click', close);
        }
        for (const opener of documentObject.querySelectorAll(selector('trigger'))) {
            opener.addEventListener('click', event => {
                if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button > 0) return;
                open(opener);
                event.preventDefault();
            });
        }
        dialog.querySelector(selector('sign-in'))?.addEventListener('click', () => {
            track('sign_in');
        });

        async function startCheckout() {
            if (busy) return false;
            setBusy(true);
            error.hidden = true;
            track('checkout_click');
            recordPurchaseStage(windowObject, source, 'cta_clicked');
            try {
                windowObject.astrolooAnalytics?.storeSubscriptionSource?.(source);
                const measurement = await checkoutMeasurementContext(windowObject);
                const response = await fetchImplementation('/api/subscription/checkout', {
                    method: 'POST',
                    referrerPolicy: 'no-referrer',
                    headers: {'Content-Type': 'application/json', Accept: 'application/json'},
                    body: JSON.stringify({source, analytics: measurement}),
                });
                if (!response.ok) {
                    const failure = new Error('Checkout request failed');
                    failure.status = response.status;
                    throw failure;
                }
                const data = await response.json();
                const destination = new URL(data.checkout_url);
                if (destination.protocol !== 'https:' || destination.hostname !== 'checkout.stripe.com'
                    || destination.username || destination.password || destination.port) {
                    throw new Error('Invalid checkout destination');
                }
                track('checkout_open');
                recordPurchaseStage(windowObject, source, 'checkout_redirected');
                windowObject.location.assign(destination.href);
                return true;
            } catch (failure) {
                track('checkout_error');
                recordPurchaseStage(windowObject, source, 'checkout_client_failed');
                setBusy(false);
                errorMessage.textContent = checkoutFailureMessage(failure?.status);
                error.hidden = false;
                error.focus();
                return false;
            }
        }

        button?.addEventListener('click', startCheckout);
        windowObject.addEventListener('pageshow', () => setBusy(false));
        return {open, close, startCheckout};
    }

    if (typeof document !== 'undefined' && typeof window !== 'undefined') {
        const init = () => {
            enhanceReadingOffer(document, window);
            enhanceReadingOffer(document, window, undefined, 'ai');
            enhanceReadingOffer(document, window, undefined, 'aspects');
        };
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', init, {once: true});
        } else {
            init();
        }
    }
    if (typeof module !== 'undefined' && module.exports) {
        module.exports = {enhanceReadingOffer, enhancePdfOffer: enhanceReadingOffer};
    }
})();
